import {
  Button,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Paper,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Grid,
  Box,
  Chip,
} from '@mui/material';
import Page from '../../../../components/page/Page';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useArticlesCore } from '../../../../../core/modules/articles/index';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMeCore } from '../../../../../core/modules/me';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useCompanyCore } from '../../../../../core/modules/company';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { debounce, isEmpty } from 'lodash';
import Spinner from '../../../../components/Spinner';
import { useMasterCore } from '../../../../../core/modules/master';
import EditHeader from '../../../../components/administration/article/EditHeader';
import Swal from 'sweetalert2';
import { ProductCategory } from '../../../../../core/interfaces/category.interface';
import { ExtraField } from '../../../../../core/interfaces/type.interface';
import { ERROR } from '../../../../../core/constants/errorCode';
import { PhotoCamera } from '@mui/icons-material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CopyButton from '../../../../components/shared/CopyButton';
import Barcode from 'react-barcode';
import { usePrintLabel } from '../../../../../core/hooks/usePrintLabel';
import PrintIcon from '@mui/icons-material/Print';
import { Article } from '../../../../../core/interfaces/article.interface';
import CategorySelect from '../../../../components/shared/CategorySelect';

interface FormData {
  name: string;
  code?: string;
  brand: string;
  alternative_code?: string;
  description?: string;
  notes?: string;
  pack?: boolean;
  price?: number | null;
  width_cm?: number | null;
  length_cm?: number | null;
  depth_cm?: number | null;
  weight_kg?: number | null;
  product_category_id?: number;
  product_type_id?: number;
  extra_fields?: Record<string, any>;
}

const EditArticle = () => {
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const {
    errorCode,
    article,
    putArticle,
    deleteArticle,
    postArticleFile,
    deleteFile,
    getArticle,
    setArticle,
  } = useArticlesCore();
  const { goToWelcome, goToArticles } = useNavigationCore();
  const { printDestinationContainer, printArticle } = usePrintLabel();
  const {
    productTypes,
    productCategories,
    genres,
    formats,
    getCategories,
    getTypes,
    getFormats,
    getGenres,
  } = useMasterCore();

  useEffect(() => {
    if (
      !user ||
      (!user.privileges.superAdmin &&
        !user.privileges.readProduct &&
        !user.privileges.writeProduct)
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  const [loading, setLoading] = useState(false);
  const [withoutCode, setWithoutCode] = useState(false);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [format, setFormat] = useState<number | undefined>();
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, any>>(
    {}
  );
  const [oldFiles, setOldFiles] = useState<{ ID: number; URL: string }[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<number[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [editedArticle, setEditedArticle] = useState<Article | null>(null);

  const { search } = useLocation();
  const queryCode = useMemo(() => new URLSearchParams(search).get('code'), [search]);
  const queryArticleId = useMemo(() => new URLSearchParams(search).get('articleId'), [search]);

  // Si no hay artículo cargado y no hay parámetros en la URL, redirigir a la lista
  // Pero solo después de que se haya intentado cargar (evitar redirección prematura)
  useEffect(() => {
    // Si ya hay un artículo en Redux, no hacer nada (se está editando)
    if (article) {
      return;
    }
    
    // Si hay parámetros en la URL (code o articleId), esperar a que se cargue el artículo
    // Estos se manejan en otros useEffect
    if (queryCode || queryArticleId) {
      return;
    }
    
    // Solo redirigir si no hay artículo ni parámetros después de un delay razonable
    // Esto permite que el artículo se cargue desde Redux si viene de otra pantalla
    const timer = setTimeout(() => {
      // Verificar nuevamente antes de redirigir
      if (!article && !queryCode && !queryArticleId) {
        goToArticles();
      }
    }, 1500); // Delay suficiente para que Redux se actualice
    
    return () => clearTimeout(timer);
  }, [article, queryCode, queryArticleId, goToArticles]);

  // Si hay articleId pero no hay artículo cargado, intentar buscar en la lista o mostrar error
  useEffect(() => {
    if (companyId && queryArticleId && !article) {
      const articleIdNum = parseInt(queryArticleId, 10);
      if (!isNaN(articleIdNum)) {
        // Mostrar mensaje de que se necesita el código para buscar
        Swal.fire({
          title: 'Información',
          text: 'Para editar este artículo, se necesita el código. Por favor, búsquelo por código.',
          icon: 'info',
          confirmButtonText: 'Ir a la lista',
        }).then(() => {
          goToArticles();
        });
      }
    }
  }, [companyId, queryArticleId, article, goToArticles]);

  useEffect(() => {
    // Cargar artículo desde código en la URL si no está ya cargado o es diferente
    if (companyId && queryCode) {
      // Si ya tenemos el artículo correcto, no hacer nada
      if (article && article.code === queryCode) {
        return;
      }
      
      getArticle(
        companyId, 
        { code: queryCode }, 
        (fetchedArticle) => {
          if (fetchedArticle) {
            setArticle(fetchedArticle);
          } else {
            Swal.fire({
              title: 'Error',
              text: `No se encontró un artículo con el código ${queryCode}`,
              icon: 'error',
              confirmButtonText: 'Aceptar',
            }).then(() => {
              goToArticles();
            });
          }
        },
        () => {
          Swal.fire({
            title: 'Error',
            text: `Error al cargar el artículo con código ${queryCode}`,
            icon: 'error',
            confirmButtonText: 'Aceptar',
          }).then(() => {
            goToArticles();
          });
        }
      );
    }
  }, [companyId, queryCode, article, getArticle, setArticle, goToArticles]);

  const [productDescription, setProductDescription] = useState('');
  const [productType, setProductType] = useState<number>();
  const [volumetricWeightKg, setVolumetricWeightKg] = useState<number>(0);
  const [width, setWidth] = useState<number>(0);
  const [length, setLength] = useState<number>(0);
  const [depth, setDepth] = useState<number>(0);
  const [prefix, setPrefix] = useState('SC');
  const [isPack, setIsPack] = useState(false);

  // Schema dinámico basado en si hay categorías disponibles para el tipo de producto seleccionado
  const schema = useMemo(() => {
    const hasCategoriesForType = productType ? productCategories.filter(
      (category) => (category as ProductCategory).product_type_id === productType
    ).length > 0 : false;

    const numericField = () =>
      yup
        .number()
        .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
        .typeError('Debe ingresar un número válido')
        .optional();

    return yup.object().shape({
      code: yup.string().optional(),
      alternative_code: yup.string().optional(),
      name: yup.string().required('El nombre del producto es obligatorio'),
      brand: yup.string().required('La marca es obligatoria'),
      notes: yup.string().optional(),
      price: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
        .typeError('Debe ingresar un número válido')
        .min(0, 'El precio no puede ser negativo')
        .nullable()
        .optional(),
      product_category_id: hasCategoriesForType
        ? yup.number().required('La categoría del producto es obligatoria')
        : yup.number().optional(),
      product_type_id: yup
        .number()
        .required('El tipo de producto es obligatorio'),
      width_cm: numericField(),
      length_cm: numericField(),
      depth_cm: numericField(),
      weight_kg: numericField(),
      extra_fields: yup.object().optional(),
    });
  }, [productCategories, productType]);

  const { control, formState, handleSubmit, resetField, setValue, trigger } =
    useForm({
      mode: 'onChange',
      resolver: yupResolver(schema),
    });
  const { isValid, errors } = formState;

  // En edición, mostrar todas las categorías para permitir cambiar a otra
  const filteredProductCategories = useMemo(() => {
    const categories = productCategories as ProductCategory[];
    if (!productType) {
      return categories;
    }
    return categories.filter(
      (category) => (category as ProductCategory).product_type_id === productType
    );
  }, [productCategories, productType]);

  // Determinar si la categoría es obligatoria
  const isCategoryRequired = filteredProductCategories.length > 0;
  
  // Determinar cuántos campos son obligatorios
  const requiredFieldsCount = isCategoryRequired ? 4 : 3;

  const resetForm = useCallback(() => {
    resetField('code');
    resetField('alternative_code');
    resetField('name');
    resetField('brand');
    resetField('notes');
    resetField('price');
    resetField('product_category_id');
    resetField('width_cm');
    resetField('length_cm');
    resetField('depth_cm');
    resetField('weight_kg');
  }, [resetField]);

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value: string, field: string) => {
        setValue(field as any, value.toUpperCase());
      }, 1000),
    [setValue]
  );

  useEffect(() => {
    let description = '';

    if (isPack) {
      description = 'PACK ';
    }

    if (name) {
      description = `${description}${name} `;
    }

    if (brand) {
      description = `${description}${brand} `;
    }

    if (format) {
      const frmt = formats.find((frmt) => frmt.id === format);
      description = `${description}${frmt?.name} `;
    }

    const type = productTypes.find((pType) => pType.id === productType);
    type?.extra_fields
      .filter((extraF) => extraF.in_description)
      .forEach((extraF) => {
        if (extraF.type === 'boolean') {
          // Para campos booleanos, solo agregar si están marcados
          if (extraFieldValues[extraF.name]) {
            description = `${description}${extraF.name} `;
          }
        } else {
          // Para otros tipos de campos, agregar si tienen valor
          const value = extraFieldValues[extraF.name];
          if (value) {
            description = `${description}${value} `;
          }
        }
      });

    setProductDescription(description);
  }, [
    brand,
    format,
    formats,
    name,
    extraFieldValues,
    productTypes,
    productType,
    isPack
  ]);

  function chunkArray(arr: any[], size = 3): ExtraField[][] {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }

  const updateExtraFieldValue = useCallback(
    (field: ExtraField, value: any) => {
      let formattedValue: any = value;
      if (field.type === 'numeric') {
        formattedValue = value === '' || value === null ? undefined : +value;
      } else if (typeof value === 'string') {
        formattedValue = value.toUpperCase();
      }

      const newExtraFieldValues = {
        ...extraFieldValues,
        [field.name]: formattedValue,
      };
      setExtraFieldValues(newExtraFieldValues);
      setValue('extra_fields', newExtraFieldValues, { shouldDirty: true, shouldValidate: true });
    },
    [extraFieldValues, setValue]
  );

  const extraFields = useMemo(() => {
    if (productType) {
      const type = productTypes.find((pType) => pType.id === productType);

      return type ? chunkArray(type.extra_fields) : [];
    }

    return [];
  }, [productType, productTypes]);

  const onSubmit = useCallback(
    (values: FormData) => {
      if (companyId && article) {
        setLoading(true);
        const payload = {
          ...values,
          pack: isPack,
          code: withoutCode ? prefix : values.code,
          description: productDescription,
          volumetric_weight_kg: volumetricWeightKg,
          product_category_id: values.product_category_id || 0,
          extra_fields: (values.extra_fields || {}) as object,
          price: values.price ?? undefined,
          width_cm: values.width_cm ?? undefined,
          length_cm: values.length_cm ?? undefined,
          depth_cm: values.depth_cm ?? undefined,
          weight_kg: values.weight_kg ?? undefined,
        };

        putArticle(
          companyId,
          article.id,
          payload,
          async (id: number) => {
            for (const file of newFiles) {
              await postArticleFile(companyId, id, file);
            }
            for (const file of filesToDelete) {
              await deleteFile(companyId, id, file);
            }
            
            // Crear el objeto del artículo editado para mostrar en el modal
            const articleData: Article = {
              id: article.id,
              name: values.name,
              code: withoutCode ? prefix : values.code || '',
              brand: values.brand,
              alternative_code: values.alternative_code,
              description: productDescription,
              notes: values.notes,
              pack: isPack,
              price: values.price || 0,
              width_cm: values.width_cm || 0,
              length_cm: values.length_cm || 0,
              depth_cm: values.depth_cm || 0,
              weight_kg: values.weight_kg || 0,
              volumetric_weight_kg: volumetricWeightKg,
              extra_fields: values.extra_fields || {},
              product_category_id: values.product_category_id || 0,
              product_id: productType || 0,
              created_at: article.created_at,
              files: [
                ...oldFiles.filter(f => !filesToDelete.includes(f.ID)),
                ...newFiles.map((_, index) => ({ ID: index + 1000, URL: URL.createObjectURL(newFiles[index]) }))
              ]
            };
            
            setEditedArticle(articleData);
            setShowSuccessModal(true);
            resetForm();
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [
      companyId,
      article,
      putArticle,
      withoutCode,
      prefix,
      isPack,
      productDescription,
      volumetricWeightKg,
      resetForm,
      goToArticles,
      newFiles,
      postArticleFile,
      filesToDelete,
      deleteFile,
      oldFiles,
      productType,
    ]
  );

  useEffect(() => {
    let description = '';

    if (isPack) {
      description = 'PACK ';
    }

    if (name) {
      description = `${description}${name} `;
    }

    if (brand) {
      description = `${description}${brand} `;
    }

    if (format) {
      const frmt = formats.find((frmt) => frmt.id === format);
      description = `${description}${frmt?.name} `;
    }

    const type = productTypes.find((pType) => pType.id === productType);
    type?.extra_fields
      .filter((extraF) => extraF.in_description)
      .forEach((extraF) => {
        const value =
          extraF.type === 'boolean' && extraFieldValues[extraF.name] == true
            ? extraF.name
            : extraFieldValues[extraF.name];

        if (value) {
          description = `${description}${value} `;
        }
      });

    setProductDescription(description);
  }, [
    brand,
    extraFieldValues,
    format,
    formats,
    name,
    productType,
    productTypes,
    isPack
  ]);

  useEffect(() => {
    if (length && width && depth) {
      setVolumetricWeightKg((length * width * depth) / 4000);
    } else {
      setVolumetricWeightKg(0);
    }
  }, [length, width, depth]);

  useEffect(() => {
    if (companyId) {
      getTypes(companyId, 'product');
      getCategories(companyId, 'product');
      getGenres(companyId);
      getFormats(companyId);
    }
  }, [companyId, getTypes, getCategories, getGenres, getFormats]);

  useEffect(() => {
    if (article) {
      setValue('code', article.code);
      setValue('product_category_id', article.product_category_id);
      setValue('name', article.name);
      setValue('brand', article.brand);
      setValue('alternative_code', article.alternative_code);
      setValue('width_cm', article.width_cm);
      setValue('length_cm', article.length_cm);
      setValue('depth_cm', article.depth_cm);
      setValue('weight_kg', article.weight_kg);
      setValue('notes', article.notes);
      setValue('price', article.price);
      setValue('extra_fields', article.extra_fields || {});

      setName(article.name);
      setBrand(article.brand);
      setWidth(article.width_cm);
      setLength(article.length_cm);
      setDepth(article.depth_cm);
      setVolumetricWeightKg(article.volumetric_weight_kg);
      setExtraFieldValues(article.extra_fields);
      setOldFiles(article.files);
      setIsPack(article.pack || false);

      const category = productCategories.find(
        (item) => item.id === article.product_category_id
      ) as ProductCategory | undefined;

      const derivedTypeId = article.product_id && article.product_id > 0
        ? article.product_id
        : category?.product_type_id;

      if (derivedTypeId) {
        setProductType(derivedTypeId);
        setValue('product_type_id' as any, derivedTypeId, { shouldValidate: true });
      } else {
        setProductType(undefined);
        setValue('product_type_id' as any, undefined, { shouldValidate: true });
      }
    }
  }, [article, productCategories, setValue]);

  const onDelete = useCallback(() => {
    Swal.fire({
      title: 'Confimar acción',
      text: `Eliminar el artículo ${article?.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si, eliminar!',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed && companyId && article) {
        setLoading(true);

        deleteArticle(
          companyId,
          article.id,
          () => {
            setLoading(false);
            resetForm();
            goToArticles();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [article, companyId, deleteArticle, goToArticles, resetForm]);



  const deleteOldPhoto = useCallback(
    (id: number) => {
      setFilesToDelete([...filesToDelete, id]);
      setOldFiles(oldFiles.filter((file) => file.ID !== id));
      trigger('name');
    },
    [filesToDelete, oldFiles, trigger]
  );

  const deleteNewPhoto = useCallback(
    (index: number) => {
      const array = [...newFiles];

      array.splice(index, 1);
      setNewFiles(array);
      trigger('name');
    },
    [newFiles, trigger]
  );

  return (
    <>
    <Page
      header={<EditHeader />}
      content={
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col p-32"
        >
          {/* Descripción sticky siempre visible */}
          <div className="sticky top-0 z-100 bg-white shadow-lg rounded-2xl p-24 mb-24 border border-gray-200" style={{zIndex: 100}}>
            <div className="flex justify-between items-start mb-16">
              <Typography variant="h6" className="font-semibold text-gray-800">
                Descripción del Producto
              </Typography>
              
                             {/* Botones de guardar y eliminar fijos arriba a la derecha */}
               <div className="flex flex-col items-end gap-2">
                 {errorCode === ERROR.ERR_409 && (
                   <Typography color="error" className="text-sm">
                     Ya existe un producto con ese código
                   </Typography>
                 )}
                 <div className="flex gap-2">
                   <Button
                     variant="contained"
                     color="error"
                     sx={{ borderRadius: 50 }}
                     disabled={loading}
                     type="button"
                     onClick={onDelete}
                     size="large"
                   >
                     <Spinner show={loading} />
                     Eliminar
                   </Button>
                   <Button
                     variant="contained"
                     color="secondary"
                     sx={{ borderRadius: 50 }}
                     disabled={!isEmpty(errors) || !isValid || loading}
                     type="submit"
                     size="large"
                   >
                     <Spinner show={loading} />
                     Guardar
                   </Button>
                 </div>
               </div>
            </div>
            <TextField
              variant="outlined"
              label="Descripción"
              disabled
              fullWidth
              value={productDescription}
              sx={{
                '& .MuiInputBase-input': {
                  color: 'rgba(0, 0, 0, 0.87)',
                  fontWeight: 500,
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(0, 0, 0, 0.6)',
                }
              }}
            />
          </div>

          <Paper className="flex w-full flex-col shadow rounded-2xl mb-32 p-32 pt-40 mt-16">
            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <div className="flex items-center gap-2 w-full">
                <Controller
                  name="code"
                  control={control}
                  render={({ field }) => (
                                         <TextField
                       {...field}
                       label="Código del Producto"
                       variant="outlined"
                       disabled={loading || withoutCode}
                       onChange={(ev) => {
                         field.onChange(ev);
                         debouncedChangeHandler(ev.target.value, 'code');
                       }}
                       error={!!errors.code}
                       helperText={errors?.code?.message}
                       fullWidth
                       InputLabelProps={{
                         shrink: !!field.value,
                       }}
                       sx={{
                         '& .MuiInputBase-root': {
                           backgroundColor: withoutCode ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                         },
                         '& .MuiInputBase-input': {
                           fontWeight: withoutCode ? 400 : 600,
                           color: withoutCode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.87)',
                         }
                       }}
                     />
                  )}
                />
                {article?.code && !withoutCode && (
                  <CopyButton
                    textToCopy={article.code}
                    tooltip="Copiar código del producto"
                  />
                )}
              </div>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={withoutCode}
                    onChange={(ev) => {
                      setWithoutCode(ev.target.checked);
                    }}
                  />
                }
                className="!w-[300px]"
                label="Sin Código"
                sx={{ zIndex: 1 }}
              />

              <FormControl variant="outlined" className="!w-[200px]">
                <InputLabel id="product-prefix-label">Prefijo</InputLabel>
                <Select
                  labelId="product-prefix-label"
                  label="Prefijo"
                  value={prefix}
                  disabled={loading || !withoutCode}
                  onChange={(ev) => {
                    setPrefix(ev.target.value);
                  }}
                >
                  <MenuItem value="SC">SC</MenuItem>
                  <MenuItem value="GN">GN</MenuItem>
                </Select>
              </FormControl>
              <div className="flex items-center gap-2 w-full">
                <Controller
                  name="alternative_code"
                  control={control}
                  render={({ field }) => (
                                         <TextField
                       {...field}
                       label="Código alternativo"
                       variant="outlined"
                       onChange={(ev) => {
                         field.onChange(ev);
                         debouncedChangeHandler(
                           ev.target.value,
                           'alternative_code'
                         );
                       }}
                       disabled={loading || !withoutCode}
                       error={!!errors.alternative_code}
                       helperText={errors?.alternative_code?.message}
                       fullWidth
                       InputLabelProps={{
                         shrink: !!field.value,
                       }}
                     />
                  )}
                />
                {article?.alternative_code && withoutCode && (
                  <CopyButton
                    textToCopy={article.alternative_code}
                    tooltip="Copiar código alternativo"
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPack}
                    onChange={(ev) => {
                      setIsPack(ev.target.checked);
                    }}
                  />
                }
                label="PACK"
                className="!mr-8"
                sx={{ zIndex: 1 }}
              />
            </div>

                        <div className="flex flex-col md:flex-row gap-24 mb-24">
              <Typography variant="body2" color="text.secondary" className="mb-16 col-span-full">
                Los campos marcados con * son obligatorios ({requiredFieldsCount} campos)
              </Typography>
            </div>

            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                                       <TextField
                       {...field}
                       label="Producto"
                       variant="outlined"
                       disabled={loading}
                       error={!!errors.name}
                       helperText={errors?.name?.message}
                       onChange={(ev) => {
                         field.onChange(ev);
                         debouncedChangeHandler(ev.target.value, 'name');
                         setName(ev.target.value.toUpperCase());
                       }}
                       required
                       fullWidth
                       InputLabelProps={{
                         shrink: !!field.value,
                       }}
                     />
                )}
              />

              <Controller
                name="brand"
                control={control}
                render={({ field }) => (
                                       <TextField
                       {...field}
                       label="Marca"
                       variant="outlined"
                       disabled={loading}
                       error={!!errors.brand}
                       helperText={errors?.brand?.message}
                       onChange={(ev) => {
                         field.onChange(ev);
                         debouncedChangeHandler(ev.target.value, 'brand');
                         setBrand(ev.target.value.toUpperCase());
                       }}
                       required
                       fullWidth
                       InputLabelProps={{
                         shrink: !!field.value,
                       }}
                     />
                )}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <FormControl variant="outlined" fullWidth required>
                <InputLabel id="product-type-label">
                  Tipo de Producto *
                </InputLabel>
                <Select
                  labelId="product-type-label"
                  label="Tipo de Producto *"
                  value={productType || ''}
                  onChange={(ev) => {
                    const selectedType = +ev.target.value;
                    setExtraFieldValues({});
                    setProductType(selectedType);
                    setValue('product_type_id' as any, selectedType, { shouldValidate: true, shouldDirty: true });
                    // Limpiar categoría cuando cambie el tipo de producto
                    setValue('product_category_id', undefined);
                    // Forzar validación del formulario para recalcular el schema
                    setTimeout(() => {
                      trigger('product_type_id' as any);
                      trigger('product_category_id');
                    }, 0);
                  }}
                  disabled={loading}
                  required
                  error={!!errors.product_type_id}
                >
                  {productTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {productType && isCategoryRequired && (
                <Controller
                  name="product_category_id"
                  control={control}
                  render={({ field }) => (
                    <CategorySelect
                      value={field.value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                        // Si se selecciona una categoría, actualizar el tipo de producto si es diferente
                        if (newValue && typeof newValue === 'number') {
                          const selectedCategory = productCategories.find(
                            (cat) => cat.id === newValue
                          );
                          if (selectedCategory && (selectedCategory as ProductCategory).product_type_id !== productType) {
                            const newProductType = (selectedCategory as ProductCategory).product_type_id;
                            setProductType(newProductType);
                            setValue('product_type_id' as any, newProductType);
                            setExtraFieldValues({});
                            // Forzar validación
                            setTimeout(() => {
                              trigger('product_category_id');
                              trigger('product_type_id' as any);
                            }, 0);
                          }
                        }
                      }}
                      categories={filteredProductCategories as ProductCategory[]}
                      label="Categoría *"
                      required
                      error={!!errors.product_category_id}
                      helperText={errors?.product_category_id?.message}
                      disabled={loading}
                    />
                  )}
                />
              )}
            </div>

            {extraFields.map((subExtraFields, index) => (
              <div
                className="grid grid-cols-1 md:grid-cols-3 gap-24 mb-24"
                key={index}
              >
                {subExtraFields.map((fieldData, sIndex) =>
                  fieldData.ref === 'format' ? (
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="format-label">Formato</InputLabel>
                      <Select
                        labelId="format-label"
                        label="Formato"
                        disabled={loading}
                        value={extraFieldValues[fieldData.name]}
                        onChange={(ev) => {
                          setFormat(ev.target.value);
                          updateExtraFieldValue(fieldData, ev.target.value);
                        }}
                      >
                        {formats.map((format) => (
                          <MenuItem key={format.id} value={format.id}>
                            {format.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : fieldData.ref === 'genre' ? (
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="genre-label">Género</InputLabel>
                      <Select
                        labelId="genre-label"
                        label="Género"
                        disabled={loading}
                        value={extraFieldValues[fieldData.name]}
                        onChange={(ev) =>
                          updateExtraFieldValue(fieldData, ev.target.value)
                        }
                      >
                        {genres.map((genre) => (
                          <MenuItem key={genre.id} value={genre.id}>
                            {genre.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : fieldData.type === 'boolean' ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={extraFieldValues[fieldData.name] || false}
                          onChange={(ev) => {
                            updateExtraFieldValue(fieldData, ev.target.checked);
                          }}
                        />
                      }
                      label={fieldData.name}
                    />
                  ) : (
                    <TextField
                      key={sIndex}
                      label={fieldData.name}
                      type={
                        fieldData.type === 'alphanumeric' ? 'text' : 'number'
                      }
                      value={extraFieldValues[fieldData.name]}
                      onChange={(ev) =>
                        updateExtraFieldValue(fieldData, ev.target.value)
                      }
                      variant="outlined"
                      disabled={loading}
                      fullWidth
                    />
                  )
                )}
              </div>
            ))}
          </Paper>

          <Paper className="flex w-full flex-col shadow rounded-2xl p-32 mt-16">
            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <Controller
                name="width_cm"
                control={control}
                render={({ field }) => (
                                     <TextField
                     {...field}
                     label="Ancho cm"
                     type="number"
                     variant="outlined"
                     disabled={loading}
                     onChange={(ev) => {
                       field.onChange(ev);
                       setWidth(+ev.target.value);
                     }}
                     error={!!errors.width_cm}
                     helperText={errors?.width_cm?.message}
                     fullWidth
                     InputLabelProps={{
                       shrink: !!field.value || field.value === 0,
                     }}
                   />
                )}
              />

              <Controller
                name="length_cm"
                control={control}
                render={({ field }) => (
                                     <TextField
                     {...field}
                     label="Largo cm"
                     type="number"
                     variant="outlined"
                     disabled={loading}
                     onChange={(ev) => {
                       field.onChange(ev);
                       setLength(+ev.target.value);
                     }}
                     error={!!errors.length_cm}
                     helperText={errors?.length_cm?.message}
                     fullWidth
                     InputLabelProps={{
                       shrink: !!field.value || field.value === 0,
                     }}
                   />
                )}
              />

              <Controller
                name="depth_cm"
                control={control}
                render={({ field }) => (
                                     <TextField
                     {...field}
                     label="Fondo cm"
                     type="number"
                     variant="outlined"
                     disabled={loading}
                     onChange={(ev) => {
                       field.onChange(ev);
                       setDepth(+ev.target.value);
                     }}
                     error={!!errors.depth_cm}
                     helperText={errors?.depth_cm?.message}
                     fullWidth
                     InputLabelProps={{
                       shrink: !!field.value || field.value === 0,
                     }}
                   />
                )}
              />

              <Controller
                name="weight_kg"
                control={control}
                render={({ field }) => (
                                     <TextField
                     {...field}
                     label="Peso kg"
                     type="number"
                     variant="outlined"
                     disabled={loading}
                     error={!!errors.weight_kg}
                     helperText={errors?.weight_kg?.message}
                     fullWidth
                     InputLabelProps={{
                       shrink: !!field.value || field.value === 0,
                     }}
                   />
                )}
              />

              <TextField
                label="Peso Volumétrico kg"
                type="number"
                variant="outlined"
                disabled={true}
                value={volumetricWeightKg}
                fullWidth
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '& .MuiInputBase-input': {
                    color: 'rgba(0, 0, 0, 0.6)',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(0, 0, 0, 0.6)',
                  }
                }}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-24 mb-24">
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                                     <TextField
                     {...field}
                     label="Observaciones"
                     variant="outlined"
                     disabled={loading}
                     error={!!errors.notes}
                     helperText={errors?.notes?.message}
                     fullWidth
                     InputLabelProps={{
                       shrink: !!field.value,
                     }}
                   />
                )}
              />

              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                                     <TextField
                     {...field}
                     label="Precio"
                     type="number"
                     variant="outlined"
                     disabled={loading}
                     error={!!errors.price}
                     helperText={errors?.price?.message}
                     InputLabelProps={{
                       shrink: !!field.value || field.value === 0,
                     }}
                   />
                )}
              />
            </div>

            <div className="mb-24 flex flex-col items-center">
              <label htmlFor="inputFiles">
                <Button
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                  className="mb-32"
                >
                  <PhotoCamera />
                  Seleccionar Fotos
                </Button>
              </label>
              <input
                id="inputFiles"
                type="file"
                accept="image/*"
                className="hidden"
                multiple
                onChange={(e) => {
                  if (e.target.files) {
                    const files = Array.from(e.target.files);
                    setNewFiles(files);
                  } else {
                    setNewFiles([]);
                  }
                  trigger('name');
                }}
              />
              <div className="flex flex-wrap justify-center gap-12 mt-4">
                {oldFiles.map((file) => (
                  <div
                    key={file.ID}
                    className="w-[100px] h-[100px] rounded shadow relative"
                  >
                    <img
                      src={file.URL}
                      alt={`Preview ${file.ID}`}
                      className="object-cover w-[100px] h-[100px]"
                    />
                    <IconButton
                      color="error"
                      aria-label="delete picture"
                      component="span"
                      className="absolute top-0 right-0"
                      onClick={() => deleteOldPhoto(file.ID)}
                    >
                      <HighlightOffIcon />
                    </IconButton>
                  </div>
                ))}
                {newFiles.map((file, index) => (
                  <div
                    key={index}
                    className="w-[100px] h-[100px] rounded shadow relative"
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index}`}
                      className="object-cover w-[100px] h-[100px]"
                    />
                    <IconButton
                      color="error"
                      aria-label="delete picture"
                      component="span"
                      className="absolute top-0 right-0"
                      onClick={() => deleteNewPhoto(index)}
                    >
                      <HighlightOffIcon />
                    </IconButton>
                  </div>
                ))}
              </div>
            </div>

            
          </Paper>
        </form>
      }
      scroll={isMobile ? 'normal' : 'content'}
    />
    
    {/* Modal de éxito */}
    <Dialog
      open={showSuccessModal}
      onClose={() => setShowSuccessModal(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          minHeight: 400,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'success.main' }}>
              ✓
            </Avatar>
            <Typography variant="h5" color="success.main" fontWeight="bold">
              ¡Producto Editado Exitosamente!
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setShowSuccessModal(false)}
            size="small"
            color="error"
          >
            ✕
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {editedArticle && (
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Información principal del producto */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Grid container spacing={3}>
                {/* Imagen del producto */}
                <Grid item xs={12} md={4}>
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    {editedArticle.files && editedArticle.files.length > 0 ? (
                      <img
                        src={editedArticle.files[0].URL}
                        alt={editedArticle.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 120,
                          height: 120,
                          bgcolor: 'grey.300',
                          fontSize: '3rem'
                        }}
                      >
                        📦
                      </Avatar>
                    )}
                  </Box>
                </Grid>

                {/* Detalles del producto */}
                <Grid item xs={12} md={8}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {editedArticle.name}
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary">
                      <strong>Marca:</strong> {editedArticle.brand}
                    </Typography>
                    
                    {editedArticle.description && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Descripción:</strong> {editedArticle.description}
                      </Typography>
                    )}
                    
                    {editedArticle.price > 0 && (
                      <Typography variant="h6" color="success.main" fontWeight="bold">
                        ${editedArticle.price.toLocaleString('es-ES')}
                      </Typography>
                    )}
                    
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {editedArticle.pack && (
                        <Chip label="PACK" color="primary" size="small" />
                      )}
                      {editedArticle.code && (
                        <Chip 
                          label={`Código: ${editedArticle.code}`} 
                          color="secondary" 
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Código de barras */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Código de Barras
              </Typography>
              
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body2" color="text.secondary">
                    Código: {editedArticle.code}
                  </Typography>
                  <CopyButton
                    textToCopy={editedArticle.code || ''}
                    tooltip="Copiar código"
                  />
                </Box>
                
                {editedArticle.code && (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'white', 
                    borderRadius: 1, 
                    border: '1px solid #e0e0e0' 
                  }}>
                    <Barcode
                      value={editedArticle.code}
                      fontSize={14}
                      height={60}
                      format="CODE128"
                      margin={0}
                      displayValue={true}
                      background="#FFFFFF"
                      textAlign="center"
                    />
                  </Box>
                )}
                
                {/* Botón de imprimir etiqueta */}
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={() => {
                    // Usar la función específica para imprimir artículos
                    printArticle(editedArticle);
                  }}
                  sx={{ mt: 2 }}
                >
                  Imprimir Etiqueta
                </Button>
              </Box>
            </Paper>

            {/* Información adicional */}
            { editedArticle && (editedArticle.width_cm > 0 || editedArticle.length_cm > 0 || editedArticle.depth_cm > 0 || editedArticle.weight_kg > 0) && <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Información Adicional
              </Typography>
              
              <Grid container spacing={2}>
                {editedArticle.width_cm > 0 && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Ancho:</strong> {editedArticle.width_cm} cm
                    </Typography>
                  </Grid>
                )}
                {editedArticle.length_cm > 0 && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Largo:</strong> {editedArticle.length_cm} cm
                    </Typography>
                  </Grid>
                )}
                {editedArticle.depth_cm > 0 && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Fondo:</strong> {editedArticle.depth_cm} cm
                    </Typography>
                  </Grid>
                )}
                {editedArticle.weight_kg > 0 && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Peso:</strong> {editedArticle.weight_kg} kg
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setShowSuccessModal(false)}
          size="large"
        >
          Cerrar
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            setShowSuccessModal(false);
            goToArticles();
          }}
          size="large"
        >
          Ir al Listado
        </Button>
      </DialogActions>
    </Dialog>
  </>
);
};

export default EditArticle;
